<script setup lang="ts">
import Button from 'primevue/button';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DayMenuView from './DayMenuView.vue';

const props = defineProps<{
  weekRange: {
    start: Date;
    end: Date;
  };
}>();

const isLoadingWeekDays = ref(false);
const { t } = useI18n();
const weekDays = ref<Date[]>([]);

function getWeekDays(startDate: Date, endDate: Date): Date[] {
  isLoadingWeekDays.value = true;
  const days: Date[] = []; // Explicitly define the array as a Date[] array
  const current = new Date(startDate);

  while (current <= endDate) {
    days.push(new Date(current)); // This line now knows that it's pushing a Date
    current.setDate(current.getDate() + 1);
  }

  isLoadingWeekDays.value = false;
  return days;
}

weekDays.value = getWeekDays(props.weekRange.start, props.weekRange.end);

watch(
  () => props.weekRange,
  (newRange) => {
    weekDays.value = getWeekDays(newRange.start, newRange.end);
  },
  { deep: true },
);

const today = new Date();
today.setHours(0, 0, 0, 0);

const isCurrentWeek = computed(() => {
  const start = new Date(props.weekRange.start);
  const end = new Date(props.weekRange.end);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return today >= start && today <= end;
});

const showPastDays = ref(false);

const pastDays = computed(() => weekDays.value.filter((day) => day < today));
const currentAndFutureDays = computed(() => weekDays.value.filter((day) => day >= today));
</script>

<template>
  <main>
    <div v-if="isLoadingWeekDays" class="flex h-full w-full items-center justify-center">
      <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
    </div>
    <div v-else class="flex flex-col gap-2 overflow-hidden">
      <div v-if="isCurrentWeek">
        <div class="mx-auto my-4 text-center">
          <Button
            v-if="pastDays.length && !showPastDays"
            icon="pi pi-history"
            :label="t('planning.showPreviousDays')"
            text
            size="small"
            @click="showPastDays = true"
          />
        </div>
        <div v-if="showPastDays">
          <div v-for="(day, index) in pastDays" :key="'past-' + index">
            <DayMenuView :date="day" />
          </div>
        </div>
        <div v-for="(day, index) in currentAndFutureDays" :key="'current-' + index">
          <DayMenuView :date="day" />
        </div>
      </div>
      <div v-else>
        <div v-for="(day, index) in weekDays" :key="index" class="">
          <!-- Render the DayMenuView for each day -->
          <DayMenuView :date="day" />
        </div>
      </div>
    </div>
  </main>
</template>

<style scoped></style>
